import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const FAQ = () => {
  const navigate = useNavigate();
  const [openQuestion, setOpenQuestion] = useState(null);

  const toggleQuestion = (id) => {
    setOpenQuestion(openQuestion === id ? null : id);
  };


  const faqData = [
    {
      category: 'Injectables',
      id: 'injectables',
      questions: [
        {
          id: 'inj-1',
          question: 'Who is a good candidate for Botox and dermal fillers?',
          answer: 'Adults looking to reduce facial wrinkles, restore volume, or enhance facial features are good candidates. Ideal candidates are in good health with realistic expectations and have no contraindications to neuromodulators or fillers.'
        },
        {
          id: 'inj-2',
          question: 'When will I see results from injectables?',
          answer: 'Dermal fillers show immediate results, with optimal appearance within a few days as swelling subsides. Botox results typically appear within 3-5 days, with full effects visible in about two weeks.'
        },
        {
          id: 'inj-3',
          question: 'How long do injectable results last?',
          answer: 'Botox typically lasts 3-6 months. Dermal fillers like Evolysse can last 6-18 months depending on the product and area treated. PRP treatments may require multiple sessions for optimal, longer-lasting results.'
        },
        {
          id: 'inj-4',
          question: 'Is there downtime with injectables?',
          answer: 'Injectable treatments generally require no downtime. Minor side effects may include temporary swelling, bruising, or redness at injection sites, which typically resolve within a few days.'
        },
        {
          id: 'inj-5',
          question: 'What should I avoid before and after injectable treatments?',
          answer: 'Before treatment, avoid blood-thinning medications, alcohol, and NSAIDs to reduce bruising. After treatment, avoid strenuous exercise for 24 hours, don\'t rub treated areas, and stay upright for 4 hours post-Botox.'
        },
        {
          id: 'inj-6',
          question: 'What is Preventative Baby Botox?',
          answer: 'Preventative Baby Botox uses smaller amounts of neuromodulator strategically placed to prevent wrinkle formation before they become deep-set. It\'s ideal for younger patients (late 20s-early 30s) wanting to maintain smooth skin and delay aging.'
        }
      ]
    },
    {
      category: 'Chemical Peels',
      id: 'chemical-peels',
      questions: [
        {
          id: 'cp-1',
          question: 'Who is a good candidate for chemical peels?',
          answer: 'If you have fine lines, wrinkles, mild scars, acne, or uneven skin tone and have fair skin and light hair, chemical peels are a good choice for you. Depending on the problem at hand, people with darker skin may also see good results with proper peel selection.'
        },
        {
          id: 'cp-2',
          question: 'When will I see the results of a chemical peel?',
          answer: 'You can typically expect to see results after the skin completes its healing process, generally within 1-2 weeks after treatment. Different types of chemical peels require different timings, with lighter peels showing faster results.'
        },
        {
          id: 'cp-3',
          question: 'How long do chemical peel results last?',
          answer: 'The duration of results varies by peel depth. Lighter peels might offer results lasting 1-2 months, while deeper peels might extend results for several months. Following post-treatment care instructions and sun protection can extend the duration of results.'
        },
        {
          id: 'cp-4',
          question: 'Can chemical peels cause downtime or side effects?',
          answer: 'The type of peel determines downtime. Light peels may not require any downtime, whereas deeper peels may require a few days to recover. Redness, dryness, and peeling of the skin are common side effects, but these subside with time.'
        },
        {
          id: 'cp-5',
          question: 'What should I do before and after a chemical peel treatment?',
          answer: 'Before a chemical peel, avoid any procedures that may irritate the skin for about a week. Stop using retinol or glycolic acid products for about 48 hours before. After treatment, keep skin clean and moisturized, avoid sun exposure, and do not pick at peeling skin.'
        },
        {
          id: 'cp-6',
          question: 'What can I expect during a chemical peel treatment?',
          answer: 'The skin is cleansed first, then a chemical solution is applied. During application, you may experience mild discomfort or stinging. The solution is then washed off or neutralized. A cool compress may be applied to soothe your skin.'
        }
      ]
    },
    {
      category: 'Facials',
      id: 'facials',
      questions: [
        {
          id: 'fac-1',
          question: 'What facial treatments do you offer?',
          answer: 'We offer HydraFacial (Signature, Deluxe, and Platinum) and Moveo Glo Laser Facial. Each treatment addresses different skin concerns with varying levels of intensity and customization for optimal results.'
        },
        {
          id: 'fac-2',
          question: 'What is the difference between HydraFacial tiers?',
          answer: 'Signature HydraFacial includes cleansing, exfoliation, extraction, and hydration. Deluxe adds personalized boosters and LED light therapy. Platinum includes all Deluxe benefits plus lymphatic drainage and additional targeted treatments for comprehensive rejuvenation.'
        },
        {
          id: 'fac-3',
          question: 'Who is a good candidate for facial treatments?',
          answer: 'Facial treatments are suitable for all skin types and anyone seeking to improve skin hydration, texture, tone, or address specific concerns like acne, fine lines, hyperpigmentation, or dullness. Treatments are customized to your skin needs.'
        },
        {
          id: 'fac-4',
          question: 'When will I see results from facial treatments?',
          answer: 'HydraFacial results are immediate, with glowing, hydrated skin right after treatment. Moveo Glo results develop over several weeks as collagen production increases, with optimal results after a series of treatments.'
        },
        {
          id: 'fac-5',
          question: 'Is there any downtime with facial treatments?',
          answer: 'HydraFacial has zero downtime. Moveo Glo may cause mild redness for a few hours but typically allows immediate return to normal activities. Both treatments are gentle and suitable for sensitive skin.'
        }
      ]
    },
    {
      category: 'Body Treatments',
      id: 'body-treatments',
      questions: [
        {
          id: 'body-1',
          question: 'What body treatments do you offer?',
          answer: 'We offer SkinPen Microneedling (basic, with PRP, or with Salmon DNA), Dermaplaning, and Morpheus 8 RF therapy. Each treatment targets different skin concerns from texture and scars to deep tissue remodeling and tightening.'
        },
        {
          id: 'body-2',
          question: 'What is the difference between SkinPen Microneedling with PRP vs Salmon DNA?',
          answer: 'Both enhance microneedling results. PRP uses your own blood platelets for healing and collagen production. Salmon DNA (Polynucleotides) provides powerful regenerative properties from marine-derived growth factors. Both reduce scars, wrinkles, and improve texture.'
        },
        {
          id: 'body-3',
          question: 'What does Morpheus 8 RF treat?',
          answer: 'Morpheus 8 combines microneedling with radiofrequency energy to target deeper skin layers, tightening skin, reducing wrinkles, minimizing acne scars, and remodeling collagen for comprehensive facial and body contouring.'
        },
        {
          id: 'body-4',
          question: 'How many treatments do I need for body treatments?',
          answer: 'Microneedling and Morpheus 8 typically require 3-6 sessions spaced 4-6 weeks apart for optimal results. Dermaplaning can be done monthly. Results are cumulative and improve with each session.'
        },
        {
          id: 'body-5',
          question: 'What is the downtime for body treatments?',
          answer: 'Dermaplaning has zero downtime. Microneedling causes redness for 1-3 days. Morpheus 8 may have 3-5 days of redness and mild swelling. All treatments allow makeup application within 24-48 hours.'
        }
      ]
    },
    {
      category: 'HydraFacial',
      id: 'hydrafacial',
      questions: [
        {
          id: 'hf-1',
          question: 'How does HydraFacial work?',
          answer: 'HydraFacial uses patented vortex technology to cleanse, exfoliate, extract impurities, and hydrate skin simultaneously. The multi-step treatment deeply cleanses pores, removes dead skin cells, and infuses skin with intensive serums for immediate, visible results.'
        },
        {
          id: 'hf-2',
          question: 'What skin concerns does HydraFacial address?',
          answer: 'HydraFacial treats fine lines, wrinkles, hyperpigmentation, brown spots, oily/congested skin, enlarged pores, and uneven texture. It\'s also excellent for maintaining healthy skin and achieving instant glow before special events.'
        },
        {
          id: 'hf-3',
          question: 'How often should I get a HydraFacial?',
          answer: 'For optimal results, monthly HydraFacial treatments are recommended. This aligns with your skin\'s natural renewal cycle and maintains consistent improvement. For specific concerns, your provider may recommend more frequent initial treatments.'
        },
        {
          id: 'hf-4',
          question: 'Is HydraFacial suitable for sensitive skin?',
          answer: 'Yes! HydraFacial is gentle and customizable for all skin types, including sensitive skin. The treatment can be adjusted in intensity and serum selection to accommodate your skin\'s specific needs and sensitivities.'
        },
        {
          id: 'hf-5',
          question: 'Can I wear makeup after HydraFacial?',
          answer: 'Yes, you can apply makeup immediately after HydraFacial. However, your skin will look so radiant and glowing that many patients choose to go makeup-free! We recommend using mineral-based makeup if you do apply it after treatment.'
        }
      ]
    },
    {
      category: 'IV Therapy',
      id: 'iv-therapy',
      questions: [
        {
          id: 'iv-1',
          question: 'What are the benefits of IV therapy?',
          answer: 'IV therapy delivers vitamins, minerals, and nutrients directly into your bloodstream for 100% absorption. Benefits include increased energy, enhanced immune function, improved hydration, faster recovery, athletic performance support, and overall wellness enhancement.'
        },
        {
          id: 'iv-2',
          question: 'How long does an IV therapy session take?',
          answer: 'Most IV therapy sessions take 30-60 minutes depending on the specific drip formulation. The process is comfortable and relaxing, allowing you to read, work, or rest during treatment in our comfortable treatment rooms.'
        },
        {
          id: 'iv-3',
          question: 'How often should I get IV therapy?',
          answer: 'Frequency depends on your goals. For wellness maintenance, monthly sessions are common. For specific concerns like immune support during illness or athletic recovery, weekly sessions may be beneficial. Your provider will create a personalized plan based on your needs.'
        },
        {
          id: 'iv-4',
          question: 'Is IV therapy safe?',
          answer: 'Yes, IV therapy is safe when administered by trained medical professionals. All our IV formulations use pharmaceutical-grade ingredients. Minor side effects may include slight bruising at the insertion site. We screen for contraindications before treatment.'
        },
        {
          id: 'iv-5',
          question: 'When will I feel the effects of IV therapy?',
          answer: 'Many people feel effects immediately or within a few hours after treatment. Benefits like increased energy and improved hydration are often noticeable right away, while other benefits accumulate with regular treatments over time.'
        }
      ]
    },
    {
      category: 'Men\'s Health',
      id: 'mens-health',
      questions: [
        {
          id: 'mh-1',
          question: 'What is Testosterone Replacement Therapy (TRT)?',
          answer: 'TRT replaces testosterone in men with low levels (hypogonadism). Available as injections, gels, or pellets, TRT can improve energy, muscle mass, sexual function, mood, bone density, and overall quality of life when properly managed.'
        },
        {
          id: 'mh-2',
          question: 'How does Apex RF therapy treat erectile dysfunction?',
          answer: 'Apex RF uses low-intensity acoustic wave therapy to improve blood flow to penile tissue, promoting natural erectile function. It\'s non-invasive, drug-free, and addresses the root cause of ED by enhancing vascular health and tissue regeneration.'
        },
        {
          id: 'mh-3',
          question: 'How many Apex RF sessions are needed?',
          answer: 'Most patients require 6 sessions (recommended protocol) scheduled 1-2 times per week for optimal results. Improvements may be noticed after a few sessions, with continued enhancement over the treatment course and beyond.'
        },
        {
          id: 'mh-4',
          question: 'Is TRT safe long-term?',
          answer: 'When properly monitored by a physician with regular blood work and health assessments, TRT is safe for long-term use. Your provider will customize dosing and monitor for any side effects, ensuring optimal therapeutic benefits.'
        },
        {
          id: 'mh-5',
          question: 'What are the symptoms of low testosterone?',
          answer: 'Symptoms include fatigue, decreased libido, erectile dysfunction, reduced muscle mass, increased body fat, mood changes, difficulty concentrating, decreased bone density, and reduced motivation. Blood tests confirm low testosterone levels for proper diagnosis.'
        }
      ]
    },
    {
      category: 'Peptides',
      id: 'peptides',
      questions: [
        {
          id: 'pep-1',
          question: 'What are peptide injections?',
          answer: 'Peptide injections are amino acid chains that signal specific biological processes. Different peptides target cellular energy (NAD+), growth hormone (Sermorelin), healing (BPC 157), skin rejuvenation (GHK-CU), metabolism (MOTS-c), energy (B12), and weight loss (Lipo Stat+).'
        },
        {
          id: 'pep-2',
          question: 'What is NAD+ and what does it do?',
          answer: 'NAD+ (Nicotinamide Adenine Dinucleotide) is essential for cellular energy production, DNA repair, and anti-aging. NAD+ injections enhance energy, mental clarity, metabolism, and support healthy aging at the cellular level by optimizing mitochondrial function.'
        },
        {
          id: 'pep-3',
          question: 'How does BPC 157 help with healing?',
          answer: 'BPC 157 (Body Protection Compound) accelerates healing of muscles, tendons, ligaments, and gut tissue. It reduces inflammation, promotes tissue repair, supports recovery from injuries or surgical procedures, and enhances overall regenerative capacity.'
        },
        {
          id: 'pep-4',
          question: 'How often do I need peptide injections?',
          answer: 'Frequency varies by peptide type and individual goals. Most peptides are administered 1-3 times per week. Your provider will create a customized protocol based on your specific health objectives, response to treatment, and desired outcomes.'
        },
        {
          id: 'pep-5',
          question: 'Are peptide injections safe?',
          answer: 'Yes, when administered by trained medical professionals, peptide therapy is safe. Peptides are naturally occurring in the body. Side effects are rare and typically mild. Dosing is personalized and monitored for optimal safety and efficacy.'
        },
        {
          id: 'pep-6',
          question: 'What is the difference between all the peptides you offer?',
          answer: 'Each peptide has unique benefits: NAD+ for cellular energy/anti-aging, Sermorelin for growth hormone/sleep, BPC 157 for healing/recovery, GHK-CU for skin rejuvenation, MOTS-c for metabolic optimization, B12 for energy/nerve function, and Lipo Stat+ for weight loss support.'
        }
      ]
    },
    {
      category: 'Muscle Sculpting',
      id: 'muscle-sculpting',
      questions: [
        {
          id: 'ms-1',
          question: 'How does Tone Muscle Sculpting work?',
          answer: 'Tone uses FDA-cleared electromagnetic technology to induce supramaximal muscle contractions—more intense than voluntary exercise. These contractions build muscle mass while simultaneously burning fat in targeted areas, sculpting and toning your physique.'
        },
        {
          id: 'ms-2',
          question: 'What areas can be treated with Tone?',
          answer: 'Tone effectively treats the abdomen, buttocks, thighs, arms, and calves. It\'s ideal for building core strength, enhancing muscle definition, lifting the buttocks, toning legs, and sculpting various body areas for athletic appearance.'
        },
        {
          id: 'ms-3',
          question: 'How many Tone sessions do I need?',
          answer: 'Optimal results typically require 4-6 sessions scheduled 2-3 times per week. Each session lasts about 30 minutes. Results continue to improve for several weeks after completing the treatment series as muscle strengthens and fat metabolizes.'
        },
        {
          id: 'ms-4',
          question: 'Is Tone Muscle Sculpting painful?',
          answer: 'Tone is not painful. You\'ll feel intense muscle contractions similar to an intensive workout, but the sensation is tolerable and adjustable to your comfort level. There\'s no downtime, and you can resume normal activities immediately after treatment.'
        },
        {
          id: 'ms-5',
          question: 'When will I see results from Tone?',
          answer: 'Some patients notice improvements after 2-3 sessions. Optimal results are visible 2-4 weeks after completing the full treatment series as muscle continues to strengthen and fat continues to metabolize. Results can be maintained with healthy lifestyle.'
        }
      ]
    },
    {
      category: 'Massage',
      id: 'massage',
      questions: [
        {
          id: 'mas-1',
          question: 'What is the difference between Swedish and Deep Tissue massage?',
          answer: 'Swedish massage uses gentle, flowing strokes for relaxation, stress relief, and improved circulation. Deep Tissue massage uses firm pressure to target deeper muscle layers, chronic pain, and stubborn knots. Both offer therapeutic benefits tailored to your needs.'
        },
        {
          id: 'mas-2',
          question: 'What is Cupping therapy?',
          answer: 'Cupping uses suction cups placed on the skin to increase blood flow, release muscle tension, reduce inflammation, and promote detoxification. It\'s effective for pain relief, muscle recovery, enhancing circulation, and releasing fascial restrictions.'
        },
        {
          id: 'mas-3',
          question: 'How often should I get massage therapy?',
          answer: 'For general wellness and stress relief, monthly massages are beneficial. For chronic pain or specific issues, weekly or bi-weekly sessions may be recommended initially. Your therapist will customize a treatment plan based on your needs and goals.'
        },
        {
          id: 'mas-4',
          question: 'Will massage be painful?',
          answer: 'Swedish massage should be relaxing and comfortable. Deep Tissue may involve some discomfort when working through tight areas, but should never be unbearable. Always communicate with your therapist to adjust pressure to your comfort level for optimal therapeutic benefit.'
        },
        {
          id: 'mas-5',
          question: 'What should I do before and after massage?',
          answer: 'Before: Arrive hydrated, avoid heavy meals, communicate any concerns or areas to focus on. After: Drink plenty of water to flush out toxins, rest if needed, avoid strenuous activity for a few hours, and apply heat or ice as recommended to maximize benefits.'
        }
      ]
    },
    {
      category: 'Laser Hair Removal',
      id: 'laser-hair-removal',
      questions: [
        {
          id: 'lhr-1',
          question: 'How does laser hair removal work?',
          answer: 'The Motus AZ+ laser delivers concentrated light energy absorbed by hair follicles, destroying them at the root to prevent regrowth. The advanced technology is fast, painless, and effective for all skin types, providing permanent hair reduction.'
        },
        {
          id: 'lhr-2',
          question: 'How many laser hair removal sessions do I need?',
          answer: 'Most patients need 6-8 sessions spaced 4-8 weeks apart for optimal results. Hair grows in cycles, and multiple sessions ensure all follicles are treated during their growth phase. Package deals of 6 sessions are available for upper and lower body.'
        },
        {
          id: 'lhr-3',
          question: 'Is laser hair removal painful?',
          answer: 'The Motus AZ+ technology is designed to be painless with advanced cooling systems. Most patients describe feeling only mild warmth or tingling during treatment, far more comfortable than waxing or other hair removal methods.'
        },
        {
          id: 'lhr-4',
          question: 'Can laser hair removal work on all skin tones?',
          answer: 'Yes! The Motus AZ+ laser is specifically designed to safely and effectively treat all skin types and tones, including darker skin that was previously difficult to treat with older laser technology. All skin tones can achieve excellent results.'
        },
        {
          id: 'lhr-5',
          question: 'What should I avoid before and after laser hair removal?',
          answer: 'Before: Avoid sun exposure, tanning, and waxing for 4-6 weeks. Shave the treatment area 24 hours before. After: Avoid sun exposure, hot showers, exercise, and harsh products for 24-48 hours. Use gentle skincare and SPF 30+ sunscreen daily.'
        }
      ]
    },
    {
      category: 'Waxing',
      id: 'waxing',
      questions: [
        {
          id: 'wax-1',
          question: 'How long does hair need to be for waxing?',
          answer: 'Hair should be at least 1/4 inch long (about the length of a grain of rice) for the wax to grip properly. This typically means 2-3 weeks of growth after shaving. Longer hair can be trimmed before waxing.'
        },
        {
          id: 'wax-2',
          question: 'How long do waxing results last?',
          answer: 'Waxing results typically last 3-6 weeks. With regular waxing every 4-6 weeks, hair grows back finer, softer, and sparser over time, extending the time between appointments and making the process more comfortable.'
        },
        {
          id: 'wax-3',
          question: 'Is waxing painful?',
          answer: 'Discomfort varies by individual pain tolerance and area being waxed. Most people experience brief discomfort during hair removal, but it becomes easier with regular waxing as hair grows back finer. Our experienced estheticians use techniques to minimize discomfort.'
        },
        {
          id: 'wax-4',
          question: 'What is the difference between Brazilian and Bikini waxing?',
          answer: 'Bikini waxing removes hair along the bikini line visible in swimwear. Brazilian waxing removes all or nearly all hair from the entire intimate area, front to back, leaving you completely smooth or with a small strip if desired.'
        },
        {
          id: 'wax-5',
          question: 'What should I do before and after waxing?',
          answer: 'Before: Exfoliate gently 24 hours before, avoid sun exposure, don\'t use retinol products. After: Avoid hot baths, exercise, tight clothing, and sun exposure for 24-48 hours. Moisturize gently and exfoliate 2-3 days later to prevent ingrown hairs.'
        }
      ]
    },
    {
      category: 'Medical Weight Loss',
      id: 'medical-weight-loss',
      questions: [
        {
          id: 'mwl-1',
          question: 'What are Semaglutide, Tirzepatide, and Retatrutide?',
          answer: 'These are FDA-approved GLP-1 medications that regulate appetite, slow digestion, and improve insulin sensitivity. Semaglutide (Ozempic/Wegovy) and Tirzepatide (Mounjaro/Zepbound) are proven weight loss medications. Retatrutide is a newer triple-receptor agonist showing promising results.'
        },
        {
          id: 'mwl-2',
          question: 'How much weight can I lose with medical weight loss?',
          answer: 'Weight loss varies individually but clinical studies show average losses of 12-15% body weight with Semaglutide, 15-20% with Tirzepatide, and up to 24% with Retatrutide over 48 weeks when combined with lifestyle modifications and proper support.'
        },
        {
          id: 'mwl-3',
          question: 'How are the medications administered?',
          answer: 'All three medications are administered as once-weekly subcutaneous injections. The injections are simple, use tiny needles, and can be self-administered at home after proper training from your provider. Injection technique training is provided.'
        },
        {
          id: 'mwl-4',
          question: 'What are the side effects?',
          answer: 'Common side effects include nausea, diarrhea, constipation, and decreased appetite—usually mild and temporary. Your provider will start with a low dose and gradually increase to minimize side effects while optimizing weight loss results.'
        },
        {
          id: 'mwl-5',
          question: 'Do I need to diet and exercise?',
          answer: 'Yes. These medications work best when combined with a balanced diet and regular exercise. They help control appetite and cravings, making it easier to maintain healthy habits. Your provider will offer comprehensive lifestyle guidance, nutritional support, and ongoing monitoring.'
        }
      ]
    },
    {
      category: 'Pigmented Lesion',
      id: 'pigmented-lesion',
      questions: [
        {
          id: 'pig-1',
          question: 'What types of pigmented lesions can be treated?',
          answer: 'Laser treatment effectively addresses age spots, sun spots, liver spots, freckles, melasma, hyperpigmentation, and other benign pigmented lesions on the face, hands, chest, décolletage, and body for clearer, more even-toned skin.'
        },
        {
          id: 'pig-2',
          question: 'How does pigmented lesion treatment work?',
          answer: 'The Motus AX laser targets melanin (pigment) in the skin using specific wavelengths of light. The pigment absorbs the energy, breaks down, and is gradually eliminated by your body\'s natural processes, revealing clearer, more even-toned skin underneath.'
        },
        {
          id: 'pig-3',
          question: 'How many treatments are needed for pigmented lesions?',
          answer: 'Most patients see significant improvement after 2-4 sessions spaced 4-6 weeks apart. The number of treatments depends on lesion size, depth, and darkness. Some lighter lesions may respond after just one session, while deeper pigmentation requires more.'
        },
        {
          id: 'pig-4',
          question: 'Is there downtime with pigmented lesion treatment?',
          answer: 'Minimal to no downtime. Treated areas may darken for a few days before flaking off naturally like coffee grounds. You can resume normal activities immediately. Sun protection is essential during and after treatment to prevent new pigmentation formation.'
        },
        {
          id: 'pig-5',
          question: 'When will I see results from pigmented lesion treatment?',
          answer: 'Pigmented areas typically darken immediately after treatment, then lighten and flake off within 1-2 weeks. Optimal results develop over 2-3 months as your skin continues to clear and regenerate, with progressive improvement after each session.'
        }
      ]
    },
    {
      category: 'Vascular Lesion',
      id: 'vascular-lesion',
      questions: [
        {
          id: 'vas-1',
          question: 'What vascular lesions can be treated with laser?',
          answer: 'Laser treatment effectively addresses spider veins, broken capillaries, rosacea, facial redness, cherry angiomas, port wine stains, and other visible blood vessels on the face, legs, chest, and body for clearer, more even-toned skin.'
        },
        {
          id: 'vas-2',
          question: 'How does vascular lesion treatment work?',
          answer: 'The Nd:YAG laser delivers precise wavelengths absorbed by blood vessels, causing them to coagulate and collapse. Your body then gradually reabsorbs the treated vessels over several weeks, reducing redness and visible vascular concerns naturally.'
        },
        {
          id: 'vas-3',
          question: 'How many vascular lesion treatments do I need?',
          answer: 'Most patients need 2-4 sessions spaced 4-6 weeks apart for optimal results. The number depends on vessel size, location, and severity. Some smaller vessels may respond after a single treatment, while larger or more extensive areas require multiple sessions.'
        },
        {
          id: 'vas-4',
          question: 'Is vascular lesion treatment painful?',
          answer: 'Most patients experience mild warming or tingling sensations during treatment. The Motus AX technology includes cooling features for comfort. Treatment is generally well-tolerated with minimal discomfort, similar to a rubber band snap sensation.'
        },
        {
          id: 'vas-5',
          question: 'What is the recovery time for vascular lesion treatment?',
          answer: 'Minimal downtime. Treated areas may appear slightly red or swollen for a few hours to 2 days. You can resume normal activities immediately. Makeup can be applied the next day. Avoid sun exposure, heat, and strenuous exercise for 48 hours post-treatment.'
        }
      ]
    },
    {
      category: 'Skin Tags',
      id: 'skin-tag',
      questions: [
        {
          id: 'st-1',
          question: 'What causes skin tags?',
          answer: 'Skin tags develop where skin rubs against skin or clothing. Common causes include friction, genetics, age, weight gain, pregnancy, diabetes, and insulin resistance. They\'re harmless benign growths but can be cosmetically bothersome or catch on jewelry and clothing.'
        },
        {
          id: 'st-2',
          question: 'How are skin tags removed?',
          answer: 'Skin tags are removed using safe medical techniques including cryotherapy (freezing), surgical excision (cutting), or cauterization (burning). The method chosen depends on tag size, location, and number. Procedures are quick, performed in-office with minimal discomfort.'
        },
        {
          id: 'st-3',
          question: 'Is skin tag removal painful?',
          answer: 'Minimal discomfort. Most methods involve brief pinching or stinging sensations lasting only seconds. Numbing may be applied for larger tags or sensitive areas. The procedure takes only minutes per tag with quick healing and minimal aftercare required.'
        },
        {
          id: 'st-4',
          question: 'Will skin tags grow back after removal?',
          answer: 'Once removed, individual skin tags do not grow back in the same location. However, you may develop new skin tags in other areas due to genetics, friction, or other contributing factors. Maintaining healthy weight can help prevent new formation.'
        },
        {
          id: 'st-5',
          question: 'What is the healing time for skin tag removal?',
          answer: 'Healing is typically quick, within 1-2 weeks. Small tags may heal in days. Keep the area clean and dry, apply antibiotic ointment if recommended, and avoid irritation. Minimal scarring occurs with professional removal by trained medical professionals.'
        }
      ]
    },
    {
      category: 'General Questions',
      id: 'general',
      questions: [
        {
          id: 'gen-1',
          question: 'How do I book an appointment?',
          answer: 'You can book an appointment by calling us at (332) 529-4781, emailing centuryaesthetics@gmail.com, or using the contact form on our website. We\'ll help you schedule a consultation to discuss your goals and create a personalized treatment plan.'
        },
        {
          id: 'gen-2',
          question: 'Do you offer free consultations?',
          answer: 'Yes! We offer free consultations for many services including injectables, facials, body treatments, medical weight loss, men\'s health, peptides, muscle sculpting, laser treatments, waxing, and skin tag removal. Contact us to schedule your complimentary consultation.'
        },
        {
          id: 'gen-3',
          question: 'What payment methods do you accept?',
          answer: 'We accept cash, all major credit cards (Visa, MasterCard, American Express, Discover), and offer CareCredit financing for eligible treatments. CareCredit provides flexible payment plans to make your aesthetic and wellness goals more affordable with special financing options.'
        },
        {
          id: 'gen-4',
          question: 'Where are you located?',
          answer: 'We\'re located at 70-64 Yellowstone Boulevard Unit D2, Forest Hills, NY 11375. We serve patients throughout Queens, Brooklyn, Manhattan, and the surrounding New York City area. Convenient parking is available, and we\'re accessible by public transportation.'
        },
        {
          id: 'gen-5',
          question: 'What are your office hours?',
          answer: 'Monday: 9:00 AM - 5:00 PM, Tuesday: 12:00 PM - 7:00 PM, Wednesday & Thursday: 8:00 AM - 4:00 PM, Friday: 8:00 AM - 2:00 PM, Saturday: 1st Saturday of Each Month, 9:00 AM - 2:00 PM. Contact us to schedule your appointment during these hours.'
        }
      ]
    }
  ];

  // const scrollToContact = () => {
  //   navigate('/');
  //   setTimeout(() => {
  //     const element = document.getElementById('contact');
  //     if (element) {
  //       const offset = 90;
  //       const elementPosition = element.getBoundingClientRect().top;
  //       const offsetPosition = elementPosition + window.pageYOffset - offset;
        
  //       window.scrollTo({
  //         top: offsetPosition,
  //         behavior: 'smooth'
  //       });
  //     }
  //   }, 100);
  // };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-white to-nude/20 pt-28">
      {/* Hero Section */}
      <section className="py-16 bg-olive-darkest">
        <div className="max-w-[1400px] mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-heading font-bold text-white mb-6">
              Frequently Asked Questions
            </h1>
            <div className="w-32 h-1 bg-gold mx-auto mb-6"></div>
            <p className="text-xl text-cream/90 max-w-3xl mx-auto leading-relaxed">
              Find answers to common questions about our services, treatments, and procedures. 
              Can't find what you're looking for? Contact us directly!
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="space-y-12">
            {faqData.map((category, categoryIndex) => (
              <motion.div
                key={category.id}
                id={category.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
                className="scroll-mt-28"
              >
                <h2 className="text-3xl font-heading font-bold text-olive-darkest mb-8 pb-4 border-b-2 border-gold">
                  {category.category}
                </h2>
                <div className="space-y-4">
                  {category.questions.map((item) => (
                    <motion.div
                      key={item.id}
                      className="bg-white rounded-xl shadow-lg overflow-hidden border border-cream/30"
                      whileHover={{ shadow: '0 10px 40px rgba(0,0,0,0.1)' }}
                    >
                      <button
                        onClick={() => toggleQuestion(item.id)}
                        className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-cream/20 transition-colors duration-300"
                      >
                        <span className="text-lg font-medium text-olive-darkest pr-8">
                          {item.question}
                        </span>
                        <ChevronDown
                          className={`w-6 h-6 text-gold flex-shrink-0 transition-transform duration-300 ${
                            openQuestion === item.id ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      <AnimatePresence>
                        {openQuestion === item.id && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <div className="px-6 py-5 bg-cream/10 border-t border-cream/30">
                              <p className="text-gray-700 leading-relaxed">
                                {item.answer}
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-olive-darkest">
        <div className="max-w-[1400px] mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              Still Have Questions?
            </h2>
            <p className="text-lg text-cream/90 max-w-2xl mx-auto mb-8 leading-relaxed">
              Our team is here to help! Contact us directly to discuss your concerns, 
              learn more about our services, or schedule your consultation.
            </p>
            <motion.button
              whileHover={{ y: -2, scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  const element = document.getElementById('contact');
                  if (element) {
                    const offset = 90;
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - offset;

                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
              className="bg-gold text-olive-darkest px-10 py-4 rounded-lg font-semibold text-lg tracking-wide hover:bg-cream transition-all duration-300 shadow-xl"
            >
              Contact Us Today
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;